import React from 'react';
import { motion } from 'framer-motion';
import { Check, Shield, Zap, Bell, FileText, Users } from 'lucide-react';
import { Button } from './ui/button';

const PricingSection = () => {
  const features = [
    { icon: FileText, text: 'Unlimited policy monitoring' },
    { icon: Zap, text: 'Instant plain-English translations' },
    { icon: Shield, text: 'Custom family risk scoring' },
    { icon: Bell, text: 'Real-time change alerts' },
    { icon: Users, text: 'Unlimited family members' },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#FDFBF7]" id="pricing" data-testid="pricing-section">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-[#0D9488] font-medium text-sm uppercase tracking-wider">Simple Pricing</span>
          <h2 
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#292524] mt-4"
            data-testid="pricing-heading"
          >
            One plan. Everything included.
          </h2>
          <p className="mt-6 text-lg text-[#78716C] max-w-xl mx-auto">
            No hidden fees. No complicated tiers. Just complete protection for your family.
          </p>
        </motion.div>

        {/* Pricing Card */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="absolute -inset-4 bg-[#0D9488]/5 rounded-[2rem] blur-2xl"></div>
          <div 
            className="relative bg-white rounded-3xl shadow-xl border border-stone-100 overflow-hidden"
            data-testid="pricing-card"
          >
            {/* Free Trial Banner */}
            <div className="bg-[#0D9488] text-white text-center py-3 px-4">
              <span className="font-medium">Start with a 14-day free trial</span>
            </div>

            <div className="p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl md:text-6xl font-heading font-semibold text-[#292524]">$19.99</span>
                  <span className="text-xl text-[#78716C]">/month</span>
                </div>
                <p className="mt-3 text-[#78716C]">after your free trial ends</p>
              </div>

              {/* Features List */}
              <div className="space-y-4 mb-10">
                {features.map((feature, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-4"
                    data-testid={`pricing-feature-${index}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#0D9488]/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-[#0D9488]" />
                    </div>
                    <span className="text-[#292524] font-medium">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Button
                size="lg"
                className="w-full rounded-full bg-[#0D9488] hover:bg-[#0F766E] text-white py-6 text-lg font-medium shadow-lg shadow-teal-900/10 hover:shadow-teal-900/20 transition-all hover:-translate-y-0.5"
                data-testid="pricing-cta"
                asChild
              >
                <a href="https://app.trysaidsimply.com/auth">Start Your Free 14-Day Trial</a>
              </Button>

              <p className="text-center text-sm text-[#78716C] mt-4">
                Cancel anytime. No questions asked.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div
          className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-[#78716C]"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-[#0D9488]" />
            <span>Cancel anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-[#0D9488]" />
            <span>Instant access</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
